import classNames from 'classnames';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Container } from 'components/Grid';
import { QuaternaryHeading } from 'components/Headings';
import ProductCard from 'components/ProductCard';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const MAX_WIDTH = 1284;
const MAX_CARD_WIDTH = MAX_WIDTH / 3 - Theme.get('spacing');

class DesktopCategories extends React.Component {
  constructor(props) {
    super(props);

    this.headerRef = React.createRef();

    this.state = {
      width: 0,
      left: 0,
    };
  }

  componentDidMount = () => {
    const firstHeader = get(this.headerRef, ['current', 'firstChild', 'firstChild']);
    if (!firstHeader) {
      return;
    }

    this.setState({
      width: firstHeader.offsetWidth,
      left: firstHeader.offsetLeft,
    });
  };

  categorySelect = (e, index) => {
    const { handleCategorySelect } = this.props;

    handleCategorySelect(index);

    this.setState({
      width: e.nativeEvent.target.parentElement.offsetWidth,
      left: e.nativeEvent.target.parentElement.offsetLeft,
    });
  };

  render() {
    const { categories, trackCTAClick, activeCategoryIndex } = this.props;
    const { width, left } = this.state;

    return (
      <div className="DesktopCategories">
        <Container>
          <div className="DesktopCategories__category-container" ref={this.headerRef}>
            {map(categories, (category, categoryIndex) => (
              <div
                className="DesktopCategories__category-header-container"
                key={`desktop-category:${categoryIndex}`}
              >
                <button
                  className={classNames('DesktopCategories__category', {
                    'DesktopCategories__category--active': categoryIndex === activeCategoryIndex,
                  })}
                  aria-label={`${I18n.localize('carousel.viewSlide', {
                    slideIndex: categoryIndex,
                  })} - ${get(category, 'categoryName')}`}
                  onClick={e => this.categorySelect(e, categoryIndex)}
                >
                  <QuaternaryHeading>{get(category, 'categoryName')}</QuaternaryHeading>
                </button>
              </div>
            ))}
            <div
              className="DesktopCategories__category-slider"
              style={{ left: `${left}px`, width: `${width}px` }}
            />
          </div>

          <div className="DesktopCategories__product-container">
            {map(
              get(get(categories, activeCategoryIndex), 'categoryCards'),
              (productCard, productIndex) => (
                <div
                  className="DesktopCategories__product"
                  key={`desktop-product-${activeCategoryIndex}:${productIndex}`}
                >
                  <ProductCard
                    {...productCard}
                    trackCTAClick={trackCTAClick}
                    productCardIndex={productIndex}
                    categoryIndex={activeCategoryIndex}
                  />
                </div>
              )
            )}
          </div>
        </Container>
        <style jsx>{`
          .DesktopCategories {
            margin: 0 auto;
            max-width: ${MAX_WIDTH}px;
          }

          .DesktopCategories__product-container {
            display: flex;
            position: relative;
            justify-content: center;
            width: 100%;
            height: 100%;
          }

          .DesktopCategories__product {
            flex: 1;
            max-width: ${MAX_CARD_WIDTH}px;
            margin: 0 ${Theme.get('spacing') * 2}px;
          }

          .DesktopCategories__product:first-child {
            margin-left: 0;
          }

          .DesktopCategories__product:last-child {
            margin-right: 0;
          }

          .DesktopCategories__category-container {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: ${Theme.get('spacing') * 6}px;
            border-bottom: solid 2px ${Theme.get('colors-black-20')};
          }

          .DesktopCategories__category-slider {
            position: absolute;
            bottom: 0px;
            display: block;
            transform: translateY(2px);
            background-color: black;
            height: 2px;
            transition: all ${Theme.get('transition-duration')}ms
              ${Theme.get('transition-timing-ease-out')};
          }

          .DesktopCategories__category-header-container {
            margin: 0 ${Theme.get('spacing') * 6}px;
          }

          .DesktopCategories__category {
            opacity: 0.8;
            padding: ${Theme.get('spacing')}px;
            background-color: transparent;
            cursor: pointer;
            border: none;
            padding: ${Theme.get('spacing') * 5}px ${Theme.get('spacing') * 5}px
              ${Theme.get('spacing') * 2}px;
            transform: translateY(2px);
            outline: inherit;
          }

          .DesktopCategories__category.focused {
            outline: 2px solid ${Theme.get('colors-black')};
          }

          .DesktopCategories__category:hover {
            opacity: 1;
            transition: opacity ${Theme.get('transition-duration')}ms ease;
          }

          .DesktopCategories__category--active {
            opacity: 1;
            transition: opacity ${Theme.get('transition-duration')}ms ease;
            border-bottom: none;
          }
        `}</style>
      </div>
    );
  }
}

DesktopCategories.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      categoryCards: PropTypes.arrayOf(
        PropTypes.shape({
          productName: PropTypes.string.isRequired,
          tagline: PropTypes.string.isRequired,
          taglineIcon: PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
          }).isRequired,
          image: PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
          }).isRequired,
          ctaLinks: PropTypes.arrayOf(
            PropTypes.shape({
              url: PropTypes.string.isRequired,
              text: PropTypes.string.isRequired,
              openInNewWindow: PropTypes.bool.isRequired,
            })
          ).isRequired,
        }).isRequired
      ),
    }).isRequired
  ).isRequired,
  trackCTAClick: PropTypes.func.isRequired,
  handleCategorySelect: PropTypes.func.isRequired,
  activeCategoryIndex: PropTypes.number.isRequired,
  activeSlideIndex: PropTypes.number.isRequired,
};

export default DesktopCategories;
